/**
 * batch-58 profiel-match (FO-429) - DE gedeelde berekening van "hoe goed past
 * deze monteur bij het door de klant gewenste profiel".
 *
 * Waarom hier en niet in de API-route: dezelfde score wordt op drie plekken
 * getoond (de beheer-tab op de relatie-detail, de sleep-/bevestigingsdialoog op
 * het planbord en de medewerker-pickers) en wordt bij het toewijzen op de
 * werkbon-audit vastgelegd. Twee implementaties zouden gegarandeerd uit elkaar
 * lopen; deze pure kern is de enige waarheid (spec 129 §2.1 + acceptatie 3).
 *
 * De kern is bewust db-vrij: de server-side lader
 * (`apps/api/src/lib/profiel-match.ts`) levert het gewenste profiel, de namen en
 * de monteur-gegevens aan.
 *
 * Regels (spec §3 + §5.1):
 *   - Geen (of een leeg) profiel  -> `heeftProfiel: false`; de UI toont niets.
 *   - Er tellen ALLEEN criteria die de klant zelf koos (skills + certificaattypes).
 *   - Een certificaat dat op de peildatum verlopen is telt NIET mee, met de
 *     vermelding "verlopen op <datum>"; een certificaat dat binnenkort verloopt
 *     telt WEL mee (het is nog geldig) met de vermelding "verloopt op <datum>",
 *     consistent met de terminologie van de harde certificaat-screening.
 *   - De uitkomst is een ZACHTE hint. Hij blokkeert nooit iets; blokkeren blijft
 *     het werk van `certificaat-screening.ts` (en van de NEN3140-screening).
 */

export type ProfielCriteriumSoort = 'VAARDIGHEID' | 'CERTIFICAAT'

/**
 * Spiegelt bewust de statuswoorden van de certificaat-screening
 * (`bepaalTypeStatus`) zodat de planner-teksten consistent blijven. `VOLDAAN` en
 * `VERLOOPT_BINNENKORT` tellen allebei als gedekt; `VERLOPEN` en `ONTBREEKT` niet.
 */
export type ProfielCriteriumStatus =
  | 'VOLDAAN'
  | 'VERLOOPT_BINNENKORT'
  | 'VERLOPEN'
  | 'ONTBREEKT'

export interface ProfielMatchCriterium {
  soort: ProfielCriteriumSoort
  /** `Skill.id` respectievelijk `CertificateType.id`. */
  id: string
  naam: string
  voldaan: boolean
  status: ProfielCriteriumStatus
  /** Vervaldatum van het geldigste certificaat (ISO); null bij vaardigheden. */
  verlooptOp: string | null
  /** Korte NL-toevoeging voor de UI, bv. `verlopen op 1 feb. 2026`. */
  toelichting: string | null
}

export interface ProfielMatch {
  /** false = de klant legde geen profiel vast; toon dan GEEN hint (spec §3). */
  heeftProfiel: boolean
  /** Aantal gedekte criteria (de teller van "3/5"). */
  gedekt: number
  /** Aantal door de klant gekozen criteria (de noemer van "3/5"). */
  gewenstTotaal: number
  /** Alle gekozen criteria: vaardigheden eerst, elk in de volgorde van de klant. */
  criteria: ProfielMatchCriterium[]
  /** Deelverzameling van `criteria` die niet voldaan is (voedt "mist: ..."). */
  ontbrekend: ProfielMatchCriterium[]
  /** Vrije toelichting van de klant (`KlantGewenstProfiel.toelichting`). */
  toelichting: string | null
}

export interface ProfielMatchGewenst {
  skillIds: ReadonlyArray<string>
  certificaatTypeIds: ReadonlyArray<string>
  toelichting: string | null
}

export interface ProfielMatchMonteurCertificaat {
  certificateTypeId: string
  /** null = onbeperkt geldig. */
  expiresAt: Date | string | null
}

export interface ProfielMatchMonteur {
  skillIds: ReadonlyArray<string>
  certificaten: ReadonlyArray<ProfielMatchMonteurCertificaat>
}

export interface ProfielMatchOpties {
  /** Beoordelingsmoment; default `new Date()`. Bij plannen: de inzetdatum. */
  peildatum?: Date
  /**
   * Venster (dagen) waarbinnen een nog geldig certificaat als
   * VERLOOPT_BINNENKORT wordt gemarkeerd. Puur cosmetisch: de score verandert er
   * niet door. Default 60 (de middelste screening-termijn).
   */
  verlooptBinnenkortDagen?: number
  /** `Skill.id` -> naam. Ontbrekende id's vallen terug op een nette placeholder. */
  skillNamen?: ReadonlyMap<string, string>
  /** `CertificateType.id` -> naam. */
  certificaatNamen?: ReadonlyMap<string, string>
}

/** Default-venster voor VERLOOPT_BINNENKORT (dagen). */
export const PROFIEL_MATCH_VERLOOPT_VENSTER_DAGEN = 60

const MS_PER_DAG = 24 * 60 * 60 * 1000

const DATUM_KORT_FMT = new Intl.DateTimeFormat('nl-NL', {
  timeZone: 'Europe/Amsterdam',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

function naarDatum(waarde: Date | string | null | undefined): Date | null {
  if (waarde == null) return null
  const d = waarde instanceof Date ? waarde : new Date(waarde)
  return Number.isNaN(d.getTime()) ? null : d
}

function formatDatum(d: Date): string {
  return DATUM_KORT_FMT.format(d)
}

/**
 * Dedupliceert met behoud van de volgorde waarin de klant de criteria koos; een
 * dubbel opgeslagen id mag de noemer niet opblazen.
 */
function uniek(ids: ReadonlyArray<string>): string[] {
  const gezien = new Set<string>()
  const uit: string[] = []
  for (const id of ids) {
    if (!id || gezien.has(id)) continue
    gezien.add(id)
    uit.push(id)
  }
  return uit
}

/** Lege uitkomst: de klant legde niets vast (of alleen een toelichting). */
function leegResultaat(toelichting: string | null): ProfielMatch {
  return {
    heeftProfiel: false,
    gedekt: 0,
    gewenstTotaal: 0,
    criteria: [],
    ontbrekend: [],
    toelichting,
  }
}

/**
 * DE match-berekening. Pure functie: zelfde invoer -> zelfde uitkomst, ongeacht
 * of de aanroeper de beheer-tab, het planbord of de audit-schrijver is.
 */
export function berekenProfielMatch(
  gewenst: ProfielMatchGewenst | null | undefined,
  monteur: ProfielMatchMonteur,
  opties: ProfielMatchOpties = {},
): ProfielMatch {
  const toelichting = gewenst?.toelichting?.trim() ? gewenst.toelichting : null
  const skillIds = uniek(gewenst?.skillIds ?? [])
  const certIds = uniek(gewenst?.certificaatTypeIds ?? [])
  if (skillIds.length === 0 && certIds.length === 0) return leegResultaat(toelichting)

  const peil = opties.peildatum ?? new Date()
  const venster = Math.max(
    0,
    opties.verlooptBinnenkortDagen ?? PROFIEL_MATCH_VERLOOPT_VENSTER_DAGEN,
  )
  const eigenSkills = new Set(monteur.skillIds)

  // Per certificaattype de "geldigste" versie onthouden: onbeperkt geldig wint,
  // daarna de datum die het verst in de toekomst ligt. Een monteur kan meerdere
  // versies van hetzelfde type hebben (de versie-keten van EmployeeCertificate);
  // hij voldoet zodra er een op de peildatum geldig is.
  const besteCert = new Map<string, { onbeperkt: boolean; tot: Date | null }>()
  for (const c of monteur.certificaten) {
    const tot = naarDatum(c.expiresAt)
    const huidig = besteCert.get(c.certificateTypeId)
    if (!huidig) {
      besteCert.set(c.certificateTypeId, { onbeperkt: tot === null, tot })
      continue
    }
    if (huidig.onbeperkt) continue
    if (tot === null) {
      besteCert.set(c.certificateTypeId, { onbeperkt: true, tot: null })
      continue
    }
    if (huidig.tot === null || tot.getTime() > huidig.tot.getTime()) {
      besteCert.set(c.certificateTypeId, { onbeperkt: false, tot })
    }
  }

  const criteria: ProfielMatchCriterium[] = []

  for (const id of skillIds) {
    const voldaan = eigenSkills.has(id)
    criteria.push({
      soort: 'VAARDIGHEID',
      id,
      naam: opties.skillNamen?.get(id) ?? 'Onbekende vaardigheid',
      voldaan,
      status: voldaan ? 'VOLDAAN' : 'ONTBREEKT',
      verlooptOp: null,
      toelichting: null,
    })
  }

  for (const id of certIds) {
    const naam = opties.certificaatNamen?.get(id) ?? 'Onbekend certificaat'
    const cert = besteCert.get(id)
    if (!cert) {
      criteria.push({
        soort: 'CERTIFICAAT',
        id,
        naam,
        voldaan: false,
        status: 'ONTBREEKT',
        verlooptOp: null,
        toelichting: null,
      })
      continue
    }
    if (cert.onbeperkt || cert.tot === null) {
      criteria.push({
        soort: 'CERTIFICAAT',
        id,
        naam,
        voldaan: true,
        status: 'VOLDAAN',
        verlooptOp: null,
        toelichting: null,
      })
      continue
    }
    const verlooptIso = cert.tot.toISOString()
    if (cert.tot.getTime() < peil.getTime()) {
      criteria.push({
        soort: 'CERTIFICAAT',
        id,
        naam,
        voldaan: false,
        status: 'VERLOPEN',
        verlooptOp: verlooptIso,
        toelichting: `verlopen op ${formatDatum(cert.tot)}`,
      })
      continue
    }
    const dagenTot = Math.ceil((cert.tot.getTime() - peil.getTime()) / MS_PER_DAG)
    const binnenkort = dagenTot <= venster
    criteria.push({
      soort: 'CERTIFICAAT',
      id,
      naam,
      voldaan: true,
      status: binnenkort ? 'VERLOOPT_BINNENKORT' : 'VOLDAAN',
      verlooptOp: verlooptIso,
      toelichting: binnenkort ? `verloopt op ${formatDatum(cert.tot)}` : null,
    })
  }

  const ontbrekend = criteria.filter((c) => !c.voldaan)
  return {
    heeftProfiel: true,
    gedekt: criteria.length - ontbrekend.length,
    gewenstTotaal: criteria.length,
    criteria,
    ontbrekend,
    toelichting,
  }
}

/** `3/5` - de kale score, zonder label. */
export function formatProfielMatchScore(match: ProfielMatch): string {
  return `${match.gedekt}/${match.gewenstTotaal}`
}

/**
 * De ontbrekende criteria als opsomming, met de reden erbij waar die er is:
 * `VCA-VOL (verlopen op 1 feb. 2026), Hoogwerker`.
 */
export function formatProfielMatchMist(match: ProfielMatch): string {
  return match.ontbrekend
    .map((c) => (c.toelichting ? `${c.naam} (${c.toelichting})` : c.naam))
    .join(', ')
}

/**
 * De compacte regel voor de bevestigings-/conflictdialoog (spec §2.2a):
 * `Profielwens Acme B.V.: 3/5 - mist VCA-VOL, Hoogwerker`.
 * Geen profiel -> lege string, zodat de aanroeper niets rendert.
 */
export function formatProfielMatchRegel(
  klantNaam: string | null | undefined,
  match: ProfielMatch,
): string {
  if (!match.heeftProfiel) return ''
  const kop = klantNaam?.trim()
    ? `Profielwens ${klantNaam.trim()}: ${formatProfielMatchScore(match)}`
    : `Profielwens klant: ${formatProfielMatchScore(match)}`
  if (match.ontbrekend.length === 0) return `${kop} - volledige match`
  return `${kop} - mist ${formatProfielMatchMist(match)}`
}

/**
 * Sorteervergelijking voor de pickers (spec §2.2b, opt-in): beste dekking eerst.
 * Vergelijkt op verhouding, daarna op absoluut aantal gedekte criteria, en valt
 * terug op het label zodat de volgorde stabiel en voorspelbaar blijft.
 * Kandidaten zonder profiel-uitkomst zakken naar onderen.
 */
export function vergelijkProfielMatch(
  a: { label: string; match?: ProfielMatch },
  b: { label: string; match?: ProfielMatch },
): number {
  const aM = a.match?.heeftProfiel ? a.match : null
  const bM = b.match?.heeftProfiel ? b.match : null
  if (aM && !bM) return -1
  if (!aM && bM) return 1
  if (aM && bM) {
    const aRatio = aM.gewenstTotaal === 0 ? 0 : aM.gedekt / aM.gewenstTotaal
    const bRatio = bM.gewenstTotaal === 0 ? 0 : bM.gedekt / bM.gewenstTotaal
    if (aRatio !== bRatio) return bRatio - aRatio
    if (aM.gedekt !== bM.gedekt) return bM.gedekt - aM.gedekt
  }
  return a.label.localeCompare(b.label, 'nl')
}
